import { useRef, useState } from 'react';
import { FolderOpen, Loader2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { isVideoMedia, MediaItem, uploadMedia } from '../lib/mediaLibrary';
import ChooseFromImagesModal from './ChooseFromImagesModal';

export type AddMediaOptionsVariant = 'dropdown' | 'rows';

interface AddMediaOptionsProps {
  businessId: string;
  /** Visual layout — dropdown panel vs full-width sheet rows. */
  variant?: AddMediaOptionsVariant;
  disabled?: boolean;
  /** Called after device upload or choose-from-images import. */
  onAdded?: (created: MediaItem[]) => void;
  /** Extra callback when the choose-from modal closes (e.g. close parent menus). */
  onDone?: () => void;
  /** Fired when Choose from images opens — parent sheets can hide so they don't cover it. */
  onChooseOpen?: () => void;
  /** Fired when Choose from images closes without completing (cancel/back). */
  onChooseClose?: () => void;
  /** Also accept video files (mp4/mov, up to 1 GB). Default false. */
  allowVideo?: boolean;
  className?: string;
}

/**
 * Shared “Upload from device” + “Choose from images” actions.
 * Used by Media Manager and the Control Centre Google quick menu.
 */
export default function AddMediaOptions({
  businessId,
  variant = 'dropdown',
  disabled = false,
  onAdded,
  onDone,
  onChooseOpen,
  onChooseClose,
  allowVideo = false,
  className = '',
}: AddMediaOptionsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showChooseFromImages, setShowChooseFromImages] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // iOS camera-roll photos (HEIC) can arrive with an empty MIME type — accept
    // them by extension instead of silently dropping the selection.
    const files = Array.from(e.target.files || []).filter(
      (f) =>
        f.type.startsWith('image/') ||
        (allowVideo && (f.type.startsWith('video/') || (!f.type && /\.(mp4|mov|m4v)$/i.test(f.name)))) ||
        (!f.type && /\.(heic|heif|jpe?g|png|gif|webp|avif)$/i.test(f.name)),
    );
    if (e.target) e.target.value = '';
    if (files.length === 0) {
      toast.error(allowVideo ? 'Please choose image or video files' : 'Please choose image files');
      return;
    }

    setUploading(true);
    // Persistent toast: parent menus may close while the native picker hands
    // files over, so give visible progress outside this component.
    const hasVideo = files.some((f) => f.type.startsWith('video/'));
    const toastId = toast.loading(
      hasVideo
        ? 'Uploading video…'
        : files.length === 1
          ? 'Uploading image…'
          : `Uploading ${files.length} images…`,
    );
    try {
      const created = await uploadMedia(businessId, files);
      const videoCount = created.filter((m) => isVideoMedia(m)).length;
      toast.success(
        videoCount > 0
          ? videoCount === created.length
            ? videoCount === 1
              ? 'Video uploaded'
              : `${videoCount} videos uploaded`
            : `${created.length} files uploaded`
          : created.length === 1
            ? 'Image uploaded'
            : `${created.length} images uploaded`,
        { id: toastId },
      );
      onAdded?.(created);
      onDone?.();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Upload failed', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const busy = disabled || uploading;
  const isRows = variant === 'rows';

  const uploadBtn = (
    <button
      type="button"
      disabled={busy}
      onClick={() => fileInputRef.current?.click()}
      className={
        isRows
          ? 'w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50'
          : 'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50'
      }
    >
      {isRows ? (
        <span className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
        </span>
      ) : uploading ? (
        <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
      ) : (
        <Upload className="h-4 w-4 text-blue-600" />
      )}
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">
          {uploading ? 'Uploading…' : 'Upload from device'}
        </span>
        <span className={`block text-gray-500 ${isRows ? 'text-xs' : 'text-[11px]'}`}>
          {allowVideo
            ? 'Add photos or videos from your phone or computer'
            : 'Add new photos from your phone or computer'}
        </span>
      </span>
    </button>
  );

  const chooseBtn = (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        onChooseOpen?.();
        setShowChooseFromImages(true);
      }}
      className={
        isRows
          ? 'w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50'
          : 'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 border-t border-gray-100 disabled:opacity-50'
      }
    >
      {isRows ? (
        <span className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
          <FolderOpen className="h-5 w-5" />
        </span>
      ) : (
        <FolderOpen className="h-4 w-4 text-blue-600" />
      )}
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">Choose from images</span>
        <span className={`block text-gray-500 ${isRows ? 'text-xs' : 'text-[11px]'}`}>
          Pick from bookings, quotes, jobs, and more
        </span>
      </span>
    </button>
  );

  return (
    <>
      <div className={className}>
        {uploadBtn}
        {chooseBtn}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={allowVideo ? 'image/*,video/mp4,video/quicktime,.mov,.m4v' : 'image/*'}
        multiple
        onChange={(e) => void handleUpload(e)}
        className="hidden"
      />
      <ChooseFromImagesModal
        isOpen={showChooseFromImages}
        businessId={businessId}
        onClose={() => {
          setShowChooseFromImages(false);
          onChooseClose?.();
        }}
        onImported={(created) => {
          setShowChooseFromImages(false);
          onAdded?.(created);
          onDone?.();
        }}
      />
    </>
  );
}
